import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { getScoreById } from '../../features/wordle/utils/scoreUtils';
import './GuessModal.css';

/**
 * GuessModal - Displays detailed guess history for a leaderboard entry
 * Shows color-coded tiles for each guess attempt
 */
export default function GuessModal({ isOpen, onClose, scoreId, targetWord }) {
  const [scoreData, setScoreData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch score data when modal opens
  useEffect(() => {
    if (isOpen && scoreId) {
      fetchScoreData();
    }
  }, [isOpen, scoreId]);

  const fetchScoreData = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getScoreById(scoreId);
      setScoreData(data);
    } catch (err) {
      console.error('Error fetching score:', err);
      setError('Failed to load guess details');
    } finally {
      setLoading(false);
    }
  };

  // Close on overlay click
  const handleOverlayClick = (e) => {
    if (e.target.className === 'guess-modal-overlay') {
      onClose();
    }
  };

  // Evaluate a guess against target word
  const evaluateGuess = (guess, target) => {
    if (!guess || !target) return [];

    const result = [];
    const targetLetters = target.toUpperCase().split('');
    const guessLetters = guess.toUpperCase().split('');
    const counted = new Array(5).fill(false);

    // First pass: mark correct positions (green)
    guessLetters.forEach((letter, i) => {
      if (letter === targetLetters[i]) {
        result[i] = 'correct';
        counted[i] = true;
      }
    });

    // Second pass: mark present letters (yellow)
    guessLetters.forEach((letter, i) => {
      if (result[i]) return; // Already marked as correct

      const targetIndex = targetLetters.findIndex((l, idx) =>
        l === letter && !counted[idx]
      );

      if (targetIndex !== -1) {
        result[i] = 'present';
        counted[targetIndex] = true;
      } else {
        result[i] = 'absent';
      }
    });

    return result;
  };

  // Format time display
  const formatTime = (milliseconds) => {
    if (!milliseconds) return '—';
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  };

  if (!isOpen) return null;

  return (
    <div className="guess-modal-overlay" onClick={handleOverlayClick}>
      <div className="guess-modal">
        {/* Header */}
        <div className="guess-modal-header">
          <h2>Guess Details</h2>
          <button
            className="guess-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="guess-modal-loading">
            <div className="spinner"></div>
            <p>Loading...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="guess-modal-error">
            <p>{error}</p>
            <button onClick={fetchScoreData} className="retry-btn">
              Try Again
            </button>
          </div>
        )}

        {/* Score Data */}
        {!loading && !error && scoreData && (
          <>
            {/* Player Info */}
            <div className="guess-modal-player">
              <span className="player-name">{scoreData.username}</span>
              <span className="player-time">
                {formatTime(scoreData.completionTimeMs)}
              </span>
            </div>

            {/* Guess Grid */}
            <div className="guess-modal-grid">
              {scoreData.guesses && scoreData.guesses.map((guess, rowIndex) => {
                const word = typeof guess === 'string' ? guess : guess.word || guess;
                const evaluation = guess.evaluation || evaluateGuess(word, targetWord || scoreData.targetWord);

                return (
                  <div key={rowIndex} className="guess-row">
                    {word.toUpperCase().split('').map((letter, colIndex) => (
                      <div
                        key={colIndex}
                        className={`guess-tile ${evaluation[colIndex] || 'absent'}`}
                      >
                        {letter}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Close Button */}
        <button className="guess-modal-button" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}

